/**
 * Block-for-block skeleton for the onboarding wizard page.
 *
 * Mirrors the live layout: workflow header bar, the desktop task-step
 * rail (mobile chip strip), and the form card + comments panel row. Per
 * the CLAUDE.md "Loading states" guidance, never render a plain
 * `Loading...` string here.
 */

import { Skeleton } from '@ordo/ui/skeleton'

export function OnboardingWizardSkeleton() {
  return (
    <div className="flex h-screen flex-col bg-background" aria-busy="true" aria-label="Loading onboarding tasks">
      <header className="flex items-center justify-between border-b bg-background px-4 py-3 md:px-6">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-8 w-8 rounded-lg" />
      </header>

      {/* Mobile: task chip strip. */}
      <div className="flex gap-2 border-b px-4 py-3 md:hidden">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-36 rounded-full" />
        ))}
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Desktop: task rail. */}
        <aside className="hidden w-72 shrink-0 border-r p-3 md:block">
          <Skeleton className="mx-3 mb-3 h-3 w-20" />
          <div className="space-y-1">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg px-3 py-2.5">
                <Skeleton className="h-6 w-6 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        </aside>

        <main className="flex-1 overflow-y-auto px-4 py-6 md:px-8">
          <div className="mx-auto flex max-w-6xl flex-col gap-5">
            <div className="space-y-2">
              <Skeleton className="h-8 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
              <section className="min-w-0 flex-1 space-y-5 rounded-lg border bg-card p-4 md:p-5">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="space-y-2">
                    <Skeleton className="h-4 w-44" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ))}
                <div className="flex justify-end">
                  <Skeleton className="h-10 w-24" />
                </div>
              </section>
              <aside className="w-full lg:w-[360px] lg:shrink-0">
                <div className="space-y-3 rounded-lg border bg-card p-4 md:p-5">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </aside>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
